import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { ROUTES } from '@app/routers';
import { isEmptyObject } from '@app/utils/isEmptyObject';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { sendMetrik } from '@app/utils/sendMetrik';
import { nextTick } from 'process';
import { useNewTabOpen, useToggle } from '@app/hooks';
import { selectors, actions } from '@app/store';
import { setRedirectUrlAfterOnboarding } from '@app/services/storageService';
import { useLazyGetClientTotalSessionsQuery } from '@app/store/api/sessions';

export function useSignupClickHandler() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectors.auth.selectUser);
  const [openNewTab] = useNewTabOpen();
  const [fetchTotalSessions] = useLazyGetClientTotalSessionsQuery();
  const {
    state: isEnrollModalOpen,
    toggle: toggleEnrollModalOpen,
    open: openEnrollModal,
  } = useToggle();

  const redirectIfEmailNotConfirmed = (sessionCount: number) => {
    if (sessionCount > 0) {
      nextTick(() => openNewTab(ROUTES['/check-email']));
    }
  };

  const addOpenEnrollParams = () => {
    const currentPath = router.pathname;
    const currentQuery = router.query;
    currentQuery['withOpenEnroll'] = 'true';

    router.push(
      {
        pathname: currentPath,
        query: currentQuery,
      },
      undefined,
      { scroll: false },
    );
  };

  const handleClickSignupButton = useCallback(async () => {
    const shouldConfirmEmail =
      Boolean(user.email) &&
      !user.isEmailConfirmed &&
      (!user.phone || !user.isPhoneConfirmed);

    sendMetrik('reachGoal', 'button_therapist');

    if (isEmptyObject(user)) {
      setRedirectUrlAfterOnboarding(
        (router.asPath as string).concat('?withOpenEnroll=true'),
      );

      addOpenEnrollParams();

      dispatch(actions.auth.setAuthModalState(true));
      return;
    }

    if (shouldConfirmEmail) {
      const totalSessions = await fetchTotalSessions({
        limit: 1,
        page: 1,
      }).unwrap();

      redirectIfEmailNotConfirmed(totalSessions.data.length);
    }

    toggleEnrollModalOpen();
  }, [user, router, setRedirectUrlAfterOnboarding, toggleEnrollModalOpen]);

  return {
    handleClickSignupButton,
    isEnrollModalOpen,
    toggleEnrollModalOpen,
    openEnrollModal,
  };
}
